import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Label,
  Button,
  Card,
  CardBody,
  CardHeader,
  FormGroup,
} from "reactstrap";
import { Calendar } from "react-feather";
import { Formik, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import Flatpickr from "react-flatpickr";
import {
  fetchCustomersList,
  fetchSessionsList,
  fetchVehiclesList,
} from "../../../slices/thunks";
import { fetchTaxList } from "../../../slices/TaxMaster/thunk";
import { createSelector } from "reselect";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import { fetchDailyTriReport } from "../../../slices/TripReport/thunk";
import Swal from "sweetalert2";

const validationSchema = Yup.object({
  customerIds: Yup.array()
    .min(1, "Customer is required")
    .required("Customer is required"),
});

const TripSeet = ({ onSubmit, onCloseForm }) => {
  const dispatch = useDispatch();

  const [totals, setTotals] = useState({
    subTotal: 0,
    cgst: 0,
    sgst: 0,
    igst: 0,
    totalGst: 0,
    grandTotal: 0,
  });

  const selector = createSelector(
    (state) => state,
    (state) => ({
      taxes: state.TaxMaster.taxes,

      customersList: state.Customer.customers,
      vehicles: state.Vehicle.vehicles,
      activeSessions: state.Session.selectedSessionYear,
    }),
  );

  const { taxes, customersList, activeSessions, vehicles } =
    useSelector(selector);

  useEffect(() => {
    dispatch(fetchTaxList({ isPagination: 0 }));
    dispatch(fetchVehiclesList({ isPagination: 0 }));
    dispatch(fetchCustomersList({ isPagination: 0 }));
    // dispatch(fetchSessionsList({ isPagination: 0, isActive: true }));
  }, [dispatch]);

  const handleDispatchTrips = (values) => {
    dispatch(fetchDailyTriReport(values));
  };

  return (
    <div
      className="page-content d-flex justify-content-center align-items-center"
      style={{ minHeight: "100vh", background: "#f8f9fa" }}
    >
      <div className="w-100" style={{ maxWidth: "1000px" }}>
        <Card className="shadow-sm">
          <CardHeader className="bg-white text-start">
            <h4 className="mb-0">Daily Trip Report</h4>
          </CardHeader>

          <CardBody>
            <Formik
              enableReinitialize
              initialValues={{
                customerIds: [],
                sessionId: activeSessions?.id || "",
                vehicleIds: [],

                fromDate: "",
                toDate: "",
              }}
              validationSchema={validationSchema}
              onSubmit={async (values, { resetForm }) => {
                const result = await Swal.fire({
                  icon: "question",
                  title: " Daily Trip Report",
                  text: "Do you want to download the Daily Trip Report?",
                  showCancelButton: true,
                  confirmButtonText: "Download",
                  cancelButtonText: "Cancel",
                  confirmButtonColor: "#28a745",
                  cancelButtonColor: "#6c757d",
                });
                if (result.isConfirmed) {
                  onSubmit && onSubmit(values);
                  dispatch(fetchDailyTriReport(values));
                  resetForm();
                  onCloseForm && onCloseForm();
                }

                // onSubmit && onSubmit(values);
                // dispatch(fetchDailyTriReport(values));
                // resetForm();
                // onCloseForm && onCloseForm();
              }}
            >
              {({ values, setFieldValue }) => (
                <Form>
                  <Row className="g-4">
                    {/* LEFT COLUMN */}

                    <FormGroup>
                      <Label className="form-label">Session</Label>
                      <input
                        type="text"
                        className="form-control bg-light"
                        disabled
                        value={activeSessions?.name || ""}
                      />
                    </FormGroup>

                    <Col md={6}>
                      <FormGroup>
                        <Label className="form-label">
                          Customer <span className="text-danger">*</span>
                        </Label>
                        <Select
                          options={[
                            { value: "ALL", label: "All" },
                            ...customersList.map((c) => ({
                              value: c.id,
                              label: c.name,
                            })),
                          ]}
                          value={
                            values.customerIds?.length === customersList.length
                              ? { value: "ALL", label: "All" }
                              : values.customerIds?.length === 1
                                ? {
                                    value: values.customerIds[0],
                                    label:
                                      customersList.find(
                                        (c) => c.id === values.customerIds[0],
                                      )?.name || "",
                                  }
                                : null
                          }
                          onChange={(opt) => {
                            if (opt?.value === "ALL") {
                              setFieldValue(
                                "customerIds",
                                customersList.map((c) => c.id),
                              );
                            } else {
                              setFieldValue(
                                "customerIds",
                                opt ? [opt.value] : [],
                              );
                            }
                          }}
                          placeholder="Select Customer"
                          classNamePrefix="react-select"
                        />
                        <ErrorMessage
                          name="customerIds"
                          component="div"
                          className="text-danger small mt-1"
                        />
                      </FormGroup>

                      {/* FROM DATE */}
                      <FormGroup style={{ position: "relative" }}>
                        <Label className="form-label">From Date</Label>
                        <div style={{ position: "relative" }}>
                          <Flatpickr
                            className="form-control pe-5"
                            value={values.fromDate}
                            options={{
                              dateFormat: "d-m-Y",
                            }}
                            onChange={(date) => {
                              const fromDateValue = date?.[0]
                                ? (() => {
                                    const d0 = date[0];

                                    const dd = String(d0.getDate()).padStart(
                                      2,
                                      "0",
                                    );
                                    const m = String(
                                      d0.getMonth() + 1,
                                    ).padStart(2, "0");
                                    const y = d0.getFullYear();

                                    return `${dd}-${m}-${y}`;
                                  })()
                                : null;

                              setFieldValue("fromDate", fromDateValue);
                            }}
                          />
                          <Calendar
                            size={18}
                            className="text-muted"
                            style={{
                              position: "absolute",
                              right: "12px",
                              top: "50%",
                              transform: "translateY(-50%)",
                              pointerEvents: "none",
                            }}
                          />
                        </div>
                      </FormGroup>
                    </Col>

                    {/* RIGHT COLUMN */}
                    <Col md={6}>
                      {/* VEHICLE */}
                      <FormGroup>
                        <Label className="form-label">Vehicle</Label>
                        <Select
                          options={[
                            { value: "ALL", label: "All" },
                            ...(vehicles || []).map((v) => ({
                              value: v.id,
                              label: v.vehicleNo,
                            })),
                          ]}
                          value={
                            values.vehicleIds?.length === vehicles.length
                              ? { value: "ALL", label: "All" }
                              : values.vehicleIds?.length === 1
                                ? {
                                    value: values.vehicleIds[0],
                                    label:
                                      vehicles.find(
                                        (v) => v.id === values.vehicleIds[0],
                                      )?.vehicleNo || "",
                                  }
                                : null
                          }
                          onChange={(opt) => {
                            if (opt?.value === "ALL") {
                              setFieldValue(
                                "vehicleIds",
                                vehicles.map((v) => v.id),
                              );
                            } else {
                              setFieldValue(
                                "vehicleIds",
                                opt ? [opt.value] : [],
                              );
                            }
                          }}
                          placeholder="Select Vehicle"
                          isClearable
                          classNamePrefix="react-select"
                        />
                      </FormGroup>

                      {/* TO DATE */}
                      <FormGroup style={{ position: "relative" }}>
                        <Label className="form-label">To Date</Label>
                        <div style={{ position: "relative" }}>
                          <Flatpickr
                            className="form-control pe-5"
                            value={values.toDate}
                            options={{
                              dateFormat: "d-m-Y",
                            }}
                            onChange={(date) => {
                              const toDateValue = date?.[0]
                                ? (() => {
                                    const d0 = date[0];

                                    const dd = String(d0.getDate()).padStart(
                                      2,
                                      "0",
                                    );
                                    const m = String(
                                      d0.getMonth() + 1,
                                    ).padStart(2, "0");
                                    const y = d0.getFullYear();

                                    return `${dd}-${m}-${y}`;
                                  })()
                                : null;

                              setFieldValue("toDate", toDateValue);
                            }}
                          />
                          <Calendar
                            size={18}
                            className="text-muted"
                            style={{
                              position: "absolute",
                              right: "12px",
                              top: "50%",
                              transform: "translateY(-50%)",
                              pointerEvents: "none",
                            }}
                          />
                        </div>
                      </FormGroup>
                    </Col>

                    {/* SUBMIT BUTTON */}
                    <Col xs={12} className="text-center mt-2">
                      <Button
                        color="success"
                        className="px-4"
                        type="submit" // ✅ IMPORTANT
                      >
                        Download Report
                      </Button>
                    </Col>
                  </Row>
                </Form>
              )}
            </Formik>
          </CardBody>
        </Card>
      </div>
    </div>
  );
};

export default TripSeet;
